import { apiClient } from "@/lib/api/client"
import type {
  BranchStatus,
  KepMatch,
  OtkBranch,
  OtkSubjectPermit,
  OtkSubjectProfile,
  OtkSubjectResponsiblePerson,
  PermitStatus,
  Posada,
  ResolveByKepResult,
  ResolveByKepSubject,
  SubjectStatus,
} from "shared"

export type {
  BranchStatus,
  KepMatch,
  OtkBranch,
  OtkSubjectPermit,
  OtkSubjectProfile,
  OtkSubjectResponsiblePerson,
  PermitStatus,
  Posada,
  ResolveByKepResult,
  ResolveByKepSubject,
  SubjectStatus,
}

// "posada appears in two representations, and they are the same field... the
// mapping is positional and one-to-one, and it is the client's, not the
// server's" — login.md, endpoint 4.
export const posadaDisplay: Record<Posada, string> = {
  КЕРІВНИК: "Керівник",
  ЗАСТУПНИК_КЕРІВНИКА: "Заступник керівника",
  ВІДПОВІДАЛЬНА_ОСОБА: "Відповідальна особа",
}

// "Адмін means exactly those two посади" — login.md Purpose §2/CD1.
export const adminGrantingPosady: Posada[] = ["КЕРІВНИК", "ЗАСТУПНИК_КЕРІВНИКА"]

// Endpoint 4 — POST /api/otk/subjects/resolve-by-kep
export async function resolveSubjectByKep(
  kepSignature: string
): Promise<ResolveByKepResult> {
  const { data } = await apiClient.post<ResolveByKepResult>(
    "/otk/subjects/resolve-by-kep",
    { kepSignature }
  )
  return data
}

export interface ConnectSubjectResult {
  subjectId: string
  role: "Адмін" | "Відповідальна особа суб'єкта ОТК"
  isFirstConnection: boolean
  paymentStatus: "НЕОПЛАЧЕНО" | "ОПЛАЧЕНО"
}

// Endpoint 5 — POST /api/otk/subjects/connect
export async function connectSubject(
  kepSignature: string
): Promise<ConnectSubjectResult> {
  const { data } = await apiClient.post<ConnectSubjectResult>(
    "/otk/subjects/connect",
    { kepSignature }
  )
  return data
}

export interface ActivationStatus {
  paymentStatus: "НЕОПЛАЧЕНО" | "ОПЛАЧЕНО"
  monobankInvoiceUrl: string
}

// Endpoint 6 — GET /api/otk/subjects/:id/activation-status
export async function getActivationStatus(
  subjectId: string
): Promise<ActivationStatus> {
  const { data } = await apiClient.get<ActivationStatus>(
    `/otk/subjects/${subjectId}/activation-status`
  )
  return data
}

// subject-profile.md API/Data #1+#2 — GET /api/otk/subject. Field names match
// otk-subjects.ts's `GET /subject` response verbatim (Ukrainian wire names,
// not translated client-side) since this route has no camelCase mapping layer.

// subject-profile.md's "Gap worth naming" note: the entity models the address
// as four separate fields; this page renders one joined string. Mirrors
// otk-subjects.ts's own server-side `displayAddress()` join order/logic
// exactly (oblast, misto, vulytsia, budynok — filtered, joined with ", ")
// since `GET /subject` returns the parts unjoined, unlike
// `resolve-by-kep`'s response.
export function displaySubjectAddress(subject: OtkSubjectProfile): string {
  return [
    subject.oblast,
    subject.mistoNaselenyiPunkt,
    subject.vulytsia,
    subject.budynok,
  ]
    .filter(Boolean)
    .join(", ")
}

export const subjectStatusLabels: Record<SubjectStatus, string> = {
  АКТИВНИЙ: "Активний",
  ВИКЛЮЧЕНИЙ: "Виключений з реєстру",
}

export const permitStatusLabels: Record<PermitStatus, string> = {
  ДІЄ: "Діє",
  ПРИЗУПИНЕНО: "Призупинено",
  ВІДКЛИКАНО: "Відкликано",
}

// subject-profile.md's "Пункти ОТК" table joins region/city/street/building
// into one "Адреса" cell — same join order/logic as displaySubjectAddress
// above, applied to a branch instead of the subject itself.
export function displayBranchAddress(branch: OtkBranch): string {
  return [branch.region, branch.city, branch.street, branch.building]
    .filter(Boolean)
    .join(", ")
}

// document-editor.md's printed `inspectionEntity` field: "always
// `subjectProfile.name` plus ЄДРПОУ in the literal format `ТОВ «Діагностика
// Плюс» (ЄДРПОУ: 41234567)`".
export function displaySubjectEntity(subject: OtkSubjectProfile): string {
  return `${subject.nazva} (ЄДРПОУ: ${subject.edrpou})`
}

// document-editor.md's printed `inspectionPlace` field: "the address of the
// OTK point resolved from the document's own пункт, falling back to the
// user's bound пункт" — approximated here as the subject's first branch,
// since this app has no separate "user's bound пункт" lookup outside a
// document's own `punktId`.
export function resolveInspectionBranch(
  subject: OtkSubjectProfile,
  punktId?: string
): OtkBranch | undefined {
  return (
    subject.branches.find((branch) => branch.registryId === punktId) ??
    subject.branches[0]
  )
}

// otk-be's `Branch.status` is the English ACTIVE/SUSPENDED/CLOSED enum, not
// the Ukrainian status strings subject-profile.md's mock table renders — no
// design-system.md/subject-profile.md source pins down these three labels
// (only the mock's uppercase "АКТИВНИЙ"/"ПРИЗУПИНЕНИЙ" strings), so these are
// this page's own labels, not values copied from a doc.
export const branchStatusLabels: Record<BranchStatus, string> = {
  ACTIVE: "Активний",
  SUSPENDED: "Призупинений",
  CLOSED: "Закритий",
}

// Endpoint 1 — GET /api/otk/subject
export async function getOtkSubject(): Promise<OtkSubjectProfile> {
  const { data } = await apiClient.get<OtkSubjectProfile>("/otk/subject")
  return data
}
